// src/components/Projects/ProjectKanbanView.tsx
import { useState, useMemo } from 'react';
import { Plus, Pencil, Trash2, Users } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useProjectSections } from '@/hooks/useProjectSections';
import { useProjectTasks } from '@/hooks/useProjectTasks';
import { useCompanyUsers } from '@/hooks/useCompanyUsers';
import { useTask } from '@/hooks/useTask';
import { normalizeFieldValue } from '@/hooks/useTaskCustomFields';
import CreateProjectTaskDialog from './CreateProjectTaskDialog';
import { TaskDetailSheet } from '@/components/Tasks/TaskDetailSheet';
import type { ProjectSection, ProjectTask } from '@/types/project';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-400',
  medium: 'bg-yellow-400',
  low: 'bg-blue-400',
};

const STATUS_DOT: Record<string, string> = {
  todo: 'bg-muted-foreground/40',
  in_progress: 'bg-blue-500',
  completed: 'bg-green-500',
  cancelled: 'bg-muted-foreground/20',
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

// ─── TaskCard inline para o Kanban ────────────────────────────────────────────
// Nota: não reutilizamos o TaskCard de src/components/Tasks/TaskCard.tsx aqui
// porque ProjectTask tem shape diferente de Task (campos de vínculo ao projeto).
// O TaskCard existente exige o tipo Task completo — usaremos uma versão leve.

const KANBAN_INTERNAL_FIELDS = ['auto_type', 'linked_sale_id', 'linked_loan_id'];

function renderKanbanFieldValue(type: string, value: any): string {
  if (value === null || value === undefined) return '—';
  switch (type) {
    case 'number':
      return Number(value).toLocaleString('pt-BR');
    case 'date':
      return new Date(String(value) + 'T12:00:00')
        .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    case 'checkbox':
      return value ? 'Sim' : 'Não';
    default:
      return String(value);
  }
}

function ProjectKanbanCard({ task, assigneeName, onTaskClick }: { task: ProjectTask; assigneeName: string | null; onTaskClick: (task: ProjectTask) => void }) {
  const isCompleted = task.status === 'completed';

  return (
    <div
      className={`bg-card border border-border rounded-md p-3 shadow-sm select-none cursor-pointer hover:border-primary/50 transition-colors ${
        isCompleted ? 'opacity-60' : ''
      }`}
      onClick={(e) => { e.stopPropagation(); onTaskClick(task); }}
    >
      {/* Prioridade */}
      {task.priority && (
        <div className="flex items-center gap-1.5 mb-2">
          <div
            className={`h-1.5 w-1.5 rounded-full ${
              PRIORITY_COLORS[task.priority] ?? 'bg-muted-foreground'
            }`}
          />
          <span className="text-xs text-muted-foreground capitalize">
            {task.priority}
          </span>
        </div>
      )}

      {/* Título */}
      <p
        className={`text-sm leading-snug ${
          isCompleted ? 'line-through text-muted-foreground' : 'font-medium'
        }`}
      >
        {task.title}
      </p>

      {/* Badge CRM */}
      {task.origin_type === 'crm' && task.origin_context && (
        <div className="flex items-center gap-1 mt-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          <Users className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{task.origin_context}</span>
        </div>
      )}

      {/* Campos personalizados */}
      {(() => {
        const cf = task.custom_fields as Record<string, any> | null | undefined;
        if (!cf) return null;
        const entries = Object.entries(cf)
          .filter(([key]) => !KANBAN_INTERNAL_FIELDS.includes(key));
        if (entries.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-1 mt-2">
            {entries.map(([key, raw]) => {
              const entry = normalizeFieldValue(raw);
              const displayVal = renderKanbanFieldValue(entry.type, entry.value);
              return (
                <div
                  key={key}
                  className="flex items-center gap-1 text-[10px] bg-muted/60 rounded px-1.5 py-0.5 text-muted-foreground max-w-full"
                >
                  <span className="font-medium truncate max-w-[60px]">{key}:</span>
                  <span className="truncate max-w-[60px]">{displayVal}</span>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Rodapé */}
      <div className="flex items-center justify-between mt-3 gap-2">
        {/* Status dot */}
        <div
          className={`h-2 w-2 rounded-full flex-shrink-0 ${
            STATUS_DOT[task.status] ?? 'bg-muted-foreground/40'
          }`}
          title={task.status}
        />

        <div className="flex items-center gap-2 ml-auto">
          {/* Prazo */}
          {task.due_date && (
            <span className="text-xs text-muted-foreground">
              {new Date(task.due_date + 'T12:00:00').toLocaleDateString(
                'pt-BR',
                { day: '2-digit', month: 'short' }
              )}
            </span>
          )}

          {/* Avatar do responsável */}
          {assigneeName && (
            <div
              className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary"
              title={assigneeName}
            >
              {getInitials(assigneeName)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── SortableCard — wrapper com dnd-kit ───────────────────────────────────────

function SortableCard({
  task,
  canEdit,
  assigneeName,
  onTaskClick,
}: {
  task: ProjectTask;
  canEdit: boolean;
  assigneeName: string | null;
  onTaskClick: (task: ProjectTask) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: !canEdit });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ProjectKanbanCard task={task} assigneeName={assigneeName} onTaskClick={onTaskClick} />
    </div>
  );
}

// ─── Coluna Kanban ─────────────────────────────────────────────────────────────

function KanbanColumn({
  section,
  tasks,
  canEdit,
  projectId,
  getAssigneeName,
  onTaskClick,
  onRenameSection,
  onDeleteSection,
}: {
  section: ProjectSection;
  tasks: ProjectTask[];
  canEdit: boolean;
  projectId: string;
  getAssigneeName: (userId: string | null) => string | null;
  onTaskClick: (task: ProjectTask) => void;
  onRenameSection?: (sectionId: string, name: string) => void;
  onDeleteSection?: (sectionId: string) => void;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(section.name);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { setNodeRef, isOver } = useDroppable({ id: section.id });

  const handleRenameConfirm = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== section.name) {
      onRenameSection?.(section.id, trimmed);
    }
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleRenameConfirm();
    if (e.key === 'Escape') {
      setRenameValue(section.name);
      setIsRenaming(false);
    }
  };

  return (
    <>
    <div
      className={`flex flex-col w-[380px] flex-shrink-0 rounded-lg border transition-colors ${
        isOver
          ? 'border-primary/50 bg-primary/5'
          : 'border-border bg-muted/30'
      }`}
    >
      {/* Cabeçalho da coluna */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border group">
        {/* Lado esquerdo: nome + contador */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isRenaming ? (
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={handleRenameConfirm}
              onKeyDown={handleRenameKeyDown}
              className="h-6 text-sm font-medium px-1 py-0"
              autoFocus
            />
          ) : (
            <>
              <span className="text-sm font-medium truncate">{section.name}</span>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {tasks.length}
              </span>
            </>
          )}
        </div>

        {/* Lado direito: ações (visíveis no hover) */}
        {canEdit && !isRenaming && (
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => { setRenameValue(section.name); setIsRenaming(true); }}
              title="Renomear seção"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive"
              onClick={() => setShowDeleteDialog(true)}
              title="Excluir seção"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground"
              onClick={() => setCreateOpen(true)}
              title="Adicionar tarefa"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Tarefas */}
      <SortableContext
        items={tasks.map((t) => t.id)}
        strategy={verticalListSortingStrategy}
      >
        <div ref={setNodeRef} className="flex flex-col gap-2 p-2 flex-1 min-h-[120px]">
          {tasks.map((task) => (
            <SortableCard
              key={task.id}
              task={task}
              canEdit={canEdit}
              assigneeName={getAssigneeName(task.assigned_to)}
              onTaskClick={onTaskClick}
            />
          ))}

          {tasks.length === 0 && (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-xs text-muted-foreground/50 select-none">
                Arraste tarefas aqui
              </p>
            </div>
          )}
        </div>
      </SortableContext>

      {/* Botão adicionar no rodapé */}
      {canEdit && (
        <button
          className="flex items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors rounded-b-lg border-t border-border"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          Adicionar tarefa
        </button>
      )}

      <CreateProjectTaskDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        projectId={projectId}
        sectionId={section.id}
        sectionName={section.name}
      />
    </div>

    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir seção "{section.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            As tarefas desta seção não serão excluídas — continuarão visíveis sem seção.
            Esta ação não pode ser desfeita.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => { onDeleteSection?.(section.id); setShowDeleteDialog(false); }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Excluir
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// ─── View principal ────────────────────────────────────────────────────────────

interface ProjectKanbanViewProps {
  projectId: string;
  showCompleted: boolean;
  canEdit: boolean;
}

export default function ProjectKanbanView({
  projectId,
  showCompleted,
  canEdit,
}: ProjectKanbanViewProps) {
  const { sections, isLoading: sectionsLoading, createSection, renameSection, deleteSection } =
    useProjectSections(projectId);
  const { tasks, tasksBySection, moveTask, isLoading: tasksLoading } =
    useProjectTasks(projectId, showCompleted);
  const { users } = useCompanyUsers();

  const getAssigneeName = (userId: string | null): string | null => {
    if (!userId) return null;
    return users.find((u) => u.id === userId)?.full_name ?? null;
  };

  const [activeTask, setActiveTask] = useState<ProjectTask | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const { data: selectedTask = null } = useTask(selectedTaskId);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Mapa taskId → sectionId para lookup rápido
  const taskSectionMap = useMemo(() => {
    const map = new Map<string, string>();
    tasks.forEach((t) => {
      if (t.project_section_id) {
        map.set(t.id, t.project_section_id);
      }
    });
    return map;
  }, [tasks]);

  // Todos os IDs draggable (tarefas)
  const allTaskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find((t) => t.id === event.active.id);
    setActiveTask(task ?? null);
  };

  const handleDragOver = (_event: DragOverEvent) => {
    // visual feedback handled per-column via useDroppable isOver
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over || active.id === over.id) return;

    const draggedTaskId = active.id.toString();
    const overedId = over.id.toString();

    // Determinar seção de destino
    // O `over.id` pode ser:
    //   a) ID de uma tarefa (drop sobre tarefa) → pegar seção dessa tarefa
    //   b) ID de uma seção (drop em coluna vazia) → usar diretamente
    const isOverTask = allTaskIds.includes(overedId);
    const targetSectionId = isOverTask
      ? (taskSectionMap.get(overedId) ?? null)
      : sections.find((s) => s.id === overedId)?.id ?? null;

    if (!targetSectionId) return;

    const currentSectionId = taskSectionMap.get(draggedTaskId);
    if (currentSectionId === targetSectionId && !isOverTask) return;

    // Calcular nova posição
    // Pegar tarefas da seção de destino ordenadas por posição
    const sectionTasks = tasksBySection(targetSectionId);
    let newPosition: number;

    if (isOverTask) {
      const overTaskIndex = sectionTasks.findIndex((t) => t.id === overedId);
      if (overTaskIndex === -1) {
        newPosition = (sectionTasks[sectionTasks.length - 1]?.position ?? 0) + 1000;
      } else {
        const before = sectionTasks[overTaskIndex - 1]?.position ?? 0;
        const after = sectionTasks[overTaskIndex]?.position ?? before + 2000;
        newPosition = (before + after) / 2;
      }
    } else {
      // Drop em coluna vazia ou abaixo de todas as tarefas
      newPosition =
        (sectionTasks[sectionTasks.length - 1]?.position ?? 0) + 1000;
    }

    try {
      await moveTask({
        taskId: draggedTaskId,
        targetSectionId,
        newPosition,
      });
    } catch {
      // erro tratado no hook
    }
  };

  if (sectionsLoading || tasksLoading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="w-72 flex-shrink-0 h-96 bg-muted animate-pulse rounded-lg"
          />
        ))}
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-muted-foreground">
          Nenhuma seção encontrada neste projeto.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          As seções são criadas automaticamente ao criar a primeira tarefa.
        </p>
      </div>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {sections.map((section) => (
            <KanbanColumn
              key={section.id}
              section={section}
              tasks={tasksBySection(section.id)}
              canEdit={canEdit}
              projectId={projectId}
              getAssigneeName={getAssigneeName}
              onTaskClick={(t) => setSelectedTaskId(t.id)}
              onRenameSection={(sectionId, name) => renameSection({ sectionId, name })}
              onDeleteSection={(sectionId) => deleteSection(sectionId)}
            />
          ))}

          {canEdit && (
            <div className="w-[280px] flex-shrink-0">
              {addingColumn ? (
                <div className="rounded-lg border border-border bg-card p-3 space-y-2">
                  <Input
                    value={newColumnName}
                    onChange={(e) => setNewColumnName(e.target.value)}
                    placeholder="Nome da seção"
                    className="h-8 text-sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newColumnName.trim()) {
                        createSection({ name: newColumnName.trim() });
                        setNewColumnName('');
                        setAddingColumn(false);
                      }
                      if (e.key === 'Escape') {
                        setNewColumnName('');
                        setAddingColumn(false);
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="h-7 text-xs"
                      disabled={!newColumnName.trim()}
                      onClick={() => {
                        if (newColumnName.trim()) {
                          createSection({ name: newColumnName.trim() });
                          setNewColumnName('');
                          setAddingColumn(false);
                        }
                      }}
                    >
                      Adicionar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => { setNewColumnName(''); setAddingColumn(false); }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setAddingColumn(true)}
                  className="w-full h-[48px] rounded-lg border border-dashed border-border bg-transparent hover:bg-muted/50 text-muted-foreground text-sm flex items-center justify-center gap-2 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Adicionar coluna
                </button>
              )}
            </div>
          )}
        </div>

        {/* Overlay — card fantasma durante o drag */}
        <DragOverlay>
          {activeTask ? (
            <div className="rotate-1 shadow-lg">
              <ProjectKanbanCard task={activeTask} assigneeName={getAssigneeName(activeTask.assigned_to)} onTaskClick={(t) => setSelectedTaskId(t.id)} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <TaskDetailSheet
        task={selectedTask}
        open={!!selectedTask && !!selectedTaskId}
        onOpenChange={(open) => { if (!open) setSelectedTaskId(null); }}
      />
    </>
  );
}
